import styles from './Patroller.module.css';

import { useState, useEffect } from "react";

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { getCertifications } from "../../api.js";
import { useAuth } from '../../AuthContext';

import { Badge } from '../Badge/Badge';
import { Certification } from "../Certification/Certification";

export const Patroller = ({ patroller, onCertify }) => {
    const [ certifications, setCertifications ] = useState([]);

    const certifiedCount = patroller.certifications?.length || 0;
    const totalCertifications = certifications?.length || 0;

    const { token } = useAuth();

    useEffect(() => {
        const fetchCertifications = async () => {
            const data = await getCertifications(token);
            const certs = data.map(cert => JSON.parse(JSON.stringify(cert)));
            
            if (data && !data.error) {
                setCertifications(certs);
            }
        };

        fetchCertifications();
    }, [ token ]);

    return (
        <Accordion
            className={ styles.patroller }
            defaultExpanded={ false }
            sx={ { width: '100%' } }
        >
            <AccordionSummary expandIcon={ <ExpandMoreIcon /> }>
                <div className={ styles.info }>
                    <Grid container alignItems="center" spacing={ 2 } sx={ { width: '100%' } }>
                        <Grid size={ { xs: 1 } }>
                            <Avatar src={ patroller.photoURL } className={ styles.headerAvatar } alt="Profile" />
                        </Grid>

                        <Grid size={ { xs: 4 } }>
                            <Typography className={ styles.gridItem }>
                                { `${ patroller.firstName || 'Missing' } ${ patroller.lastName || 'Name' }` }
                            </Typography>
                        </Grid>

                        <Grid size={ { xs: 3 } }>
                            <Chip
                                label={ `${ certifiedCount }/${ totalCertifications } certs` }
                                color={ certifiedCount > 0 ? 'primary' : 'default' }
                                size="small"
                            />
                        </Grid>

                        <Grid size={ { xs: 4 } }>
                            <div style={ { display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-start' } }>
                                { patroller.certifications?.map(cert => (
                                    <div key={ cert.id } style={ { width: '30px', height: '30px' } }>
                                        <Badge certification={ cert } certified={ true } loading={ false } />
                                    </div>
                                )) }
                            </div>
                        </Grid>
                    </Grid>
                </div>
            </AccordionSummary>

            <AccordionDetails>
                <div className={ styles.details }>
                    <Typography variant="h6" gutterBottom>Patroller Details</Typography>

                    <Grid container spacing={ 2 }>
                        <Grid size={ { xs: 12, sm: 6 } }>
                            <Typography><strong>Name:</strong> { `${ patroller.firstName || 'Missing' } ${ patroller.lastName || 'Name' }` }</Typography>
                        </Grid>

                        <Grid size={ { xs: 12, sm: 6 } }>
                            <Typography><strong>Email:</strong> { patroller.email || 'Not provided' }</Typography>
                        </Grid>

                        <Grid size={ { xs: 12, sm: 6 } }>
                            <Typography><strong>Phone:</strong> { patroller.phoneNumber || 'Not provided' }</Typography>
                        </Grid>
                    </Grid>

                    <Typography variant="h6" gutterBottom sx={ { mt: 3 } }>Certifications</Typography>

                    {/* Primary Certifications */ }
                    { certifications?.filter(c => c.type === 'primary').length > 0 && (
                        <>
                            <Typography variant="subtitle1" sx={ { mt: 2, mb: 1, fontWeight: 'bold', color: 'primary.main' } }>
                                Primary Certifications
                            </Typography>

                            <Grid container spacing={ 2 }>
                                { certifications?.filter(c => c.type === 'primary').map(c => (
                                    <Grid size={ { xs: 6, sm: 4, md: 3 } } key={ c.id }>
                                        <Certification certification={ c } patroller={ patroller } onCertify={ onCertify } />
                                    </Grid>
                                )) }
                            </Grid>
                        </>
                    ) }

                    {/* Secondary/Endorsement Certifications */ }
                    { certifications?.filter(c => c.type !== 'primary').length > 0 && (
                        <>
                            <Typography variant="subtitle1" sx={ { mt: 3, mb: 1, fontWeight: 'bold', color: 'secondary.main' } }>
                                Endorsements & Secondary Certifications
                            </Typography>

                            <Grid container spacing={ 2 }>
                                { certifications?.filter(c => c.type !== 'primary').map(c => (
                                    <Grid size={ { xs: 6, sm: 4, md: 3 } } key={ c.id }>
                                        <Certification certification={ c } patroller={ patroller } onCertify={ onCertify } />
                                    </Grid>
                                )) }
                            </Grid>
                        </>
                    ) }

                    {/* Show message if no certifications */ }
                    { (!certifications || certifications.length === 0) && (
                        <Typography variant="body2" color="text.secondary" sx={ { mt: 1 } }>
                            No certifications available
                        </Typography>
                    ) }
                </div>
            </AccordionDetails>
        </Accordion>
    );
};